/**
 * Theme Loader Utility
 *
 * Generic theme loading system that works with any variable structure.
 * Loads theme CSS files and skin data from backend static directory.
 * Applies skin values to CSS custom properties.
 */

import { debugError, debugLog } from "./debug";

export interface SkinData {
  name: string;
  displayName: string;
  version: string;
  theme: string;
  description?: string;
  variables: Record<string, Record<string, string | number>>;
}

// Backend URL - adjust based on environment
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5444';

// Default skin to fallback to if custom skin fails to load
export const DEFAULT_SKIN = 'default-purple';

// localStorage key for tracking current skin
export const THEME_STORAGE_KEY = 'theme-current-skin';
// Version key to detect upgrades and invalidate cache
const THEME_VERSION_KEY = 'theme-system-version';
// Bump this version when theme system changes require cache invalidation
const CURRENT_THEME_VERSION = '2';

// Track CSS variables set by the current skin (for cleanup during skin switch)
let currentSkinVariables: Set<string> = new Set();

/**
 * Check if theme system was upgraded and clear old cache if needed
 */
function checkAndMigrateThemeCache(): void {
  try {
    const storedVersion = localStorage.getItem(THEME_VERSION_KEY);
    if (storedVersion !== CURRENT_THEME_VERSION) {
      debugLog(1, `[ThemeLoader] Theme system upgraded (${storedVersion} -> ${CURRENT_THEME_VERSION}), clearing cache`);

      // Remove all existing theme CSS link elements from DOM
      const themeLinks = document.querySelectorAll('link[id^="theme-"]');
      themeLinks.forEach(link => {
        link.remove();
        debugLog(1, `[ThemeLoader] Removed old CSS link: ${link.id}`);
      });

      // Clear old theme data to force fresh load
      localStorage.removeItem(THEME_STORAGE_KEY);
      localStorage.setItem(THEME_VERSION_KEY, CURRENT_THEME_VERSION);
    }
  } catch (error) {
    debugError('[ThemeLoader] Failed to check theme version:', error);
  }
}

/**
 * Get previously loaded skin name from localStorage
 */
function getPreviousSkin(): string | null {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch (error) {
    debugError('[ThemeLoader] Failed to read from localStorage:', error);
    return null;
  }
}

/**
 * Store current skin name in localStorage
 */
function storeSkinName(skinName: string): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, skinName);
  } catch (error) {
    debugError('[ThemeLoader] Failed to write to localStorage:', error);
  }
}

/**
 * Remove theme CSS link elements from DOM
 */
function removeThemeCSS(themeName: string): void {
  const ids = [
    `theme-variables-${themeName}`,
    `theme-layout-${themeName}`
  ];

  ids.forEach(id => {
    const element = document.getElementById(id);
    if (element) {
      element.remove();
      debugLog(1, `[ThemeLoader] Removed old CSS: ${id}`);
    }
  });
}

/**
 * Clear all CSS variables that were set by the previous skin
 * This ensures variables from old skins don't leak into new skins
 */
function clearSkinVariables(): void {
  const root = document.documentElement;

  currentSkinVariables.forEach(varName => {
    root.style.removeProperty(varName);
  });

  debugLog(1, `[ThemeLoader] Cleared ${currentSkinVariables.size} skin variables`);
  currentSkinVariables.clear();
}

/**
 * Load a CSS file dynamically and inject it into the DOM
 */
function loadCSS(url: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = url;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load CSS: ${url}`));

    document.head.appendChild(link);
  });
}

/**
 * Load theme CSS files from backend
 */
async function loadThemeCSS(themeName: string): Promise<void> {
  const baseUrl = `${BACKEND_URL}/static/themes/${themeName}`;
  // Add version as cache-buster to ensure fresh CSS on upgrades
  const cacheBuster = `?v=${CURRENT_THEME_VERSION}`;

  // Load CSS files in order: variables -> layout (includes responsive)
  await loadCSS(`${baseUrl}/theme-variables.css${cacheBuster}`, `theme-variables-${themeName}`);
  await loadCSS(`${baseUrl}/theme-layout.css${cacheBuster}`, `theme-layout-${themeName}`);
}

/**
 * Fetch skin data from backend
 * If loading fails and this is not already the default skin, retry with default skin
 */
async function loadSkinData(skinName: string, isRetry: boolean = false): Promise<SkinData> {
  const url = `${BACKEND_URL}/static/skins/${skinName}/skin.json`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load skin data: ${url} (${response.status})`);
    }

    return response.json();
  } catch (error) {
    // If this is not already the default skin and not a retry, try loading default skin
    if (!isRetry && skinName !== DEFAULT_SKIN) {
      debugError(`[ThemeLoader] Failed to load skin "${skinName}", falling back to default skin "${DEFAULT_SKIN}"`, error);
      return loadSkinData(DEFAULT_SKIN, true);
    }

    // If we're already trying default skin or this is a retry, throw the error
    throw error;
  }
}

/**
 * Check if a value looks like an image filename
 */
function isImageFilename(value: string): boolean {
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'];
  const lowerValue = value.toLowerCase();
  return imageExtensions.some(ext => lowerValue.endsWith(ext));
}

/**
 * Convert image path to full URL if needed
 *
 * Supports flexible paths like "images/Day.png" or "assets/icons/star.svg"
 */
function processImageValue(value: string, skinName: string, sectionName: string): string {
  // If it's already a url() or starts with http//, leave it as-is
  if (value.startsWith('url(') || value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  // If section is "img" and value looks like an image path, convert to URL
  if (sectionName === 'img' && isImageFilename(value)) {
    // Value can include subfolder (e.g., "images/Day.png")
    const imageUrl = `${BACKEND_URL}/static/skins/${skinName}/${value}`;
    return `url('${imageUrl}')`;
  }

  // Otherwise return value as-is
  return value;
}

/**
 * Apply skin variables to CSS custom properties (generic approach)
 *
 * Iterates through all sections in variables and sets CSS properties
 * in the format: --<section-name>-<variable-name>
 *
 * Tracks all set variables for cleanup when switching skins.
 */
function applySkinVariables(skinData: SkinData, skinName: string): void {
  const root = document.documentElement;

  if (!skinData.variables) {
    debugError('[ThemeLoader] No variables found in skin data');
    return;
  }

  // Clear tracking set for new skin
  const newVariables: Set<string> = new Set();

  // Iterate through each section (color, img, font-family, etc.)
  Object.entries(skinData.variables).forEach(([sectionName, variables]) => {
    // Iterate through each variable in the section
    Object.entries(variables).forEach(([variableName, value]) => {
      // Construct CSS variable name: --<section>-<name>
      const cssVarName = `--${sectionName}-${variableName}`;

      // Process the value (special handling for images)
      const processedValue = processImageValue(String(value), skinName, sectionName);

      // Set the CSS custom property
      root.style.setProperty(cssVarName, processedValue);

      // Track this variable for future cleanup
      newVariables.add(cssVarName);
    });
  });

  // Update the tracking set
  currentSkinVariables = newVariables;
  debugLog(1, `[ThemeLoader] Set ${newVariables.size} skin variables`);
}

/**
 * Main initialization function
 *
 * @param skinName - Name of the skin to load
 */
export async function initializeTheme(skinName: string): Promise<void> {
  try {
    // Check for theme system upgrade and clear old cache if needed
    checkAndMigrateThemeCache();

    debugLog(1, `[ThemeLoader] Initializing theme with skin: ${skinName}`);
    // Check if skin has changed
    const previousSkin = getPreviousSkin();
    const skinChanged = previousSkin && previousSkin !== skinName;
    let previousThemeName: string | null = null;

    if (skinChanged) {
      debugLog(1, `[ThemeLoader] Skin changed from ${previousSkin} to ${skinName}`);
      // Get previous theme name for later cleanup (but don't remove yet!)
      if (previousSkin) {
        try {
          const prevSkinData = await loadSkinData(previousSkin);
          previousThemeName = prevSkinData.theme;
        } catch (error) {
          debugError(`[ThemeLoader] Could not load previous skin data for cleanup:`, error);
        }
      }
    } else if (previousSkin === skinName) {
      debugLog(1, `[ThemeLoader] Skin unchanged (${skinName}), using cached CSS`);
    }

    // 1. Load skin data (may fallback to default skin if requested skin fails)
    const skinData = await loadSkinData(skinName);
    debugLog(1, `[ThemeLoader] Loaded skin data:`, skinData);

    // 2. Get actual loaded skin name (important if fallback occurred)
    const actualSkinName = skinData.name;
    if (actualSkinName !== skinName) {
      debugLog(1, `[ThemeLoader] Using fallback skin: ${actualSkinName} (requested: ${skinName})`);
    }

    // 3. Get theme name from skin data
    const themeName = skinData.theme;
    debugLog(1, `[ThemeLoader] Skin requires theme: ${themeName}`);

    // 4. Load NEW theme CSS files FIRST (before removing old)
    // This ensures CSS is always available - no flash of unstyled content
    await loadThemeCSS(themeName);
    debugLog(1, `[ThemeLoader] Loaded theme CSS files for: ${themeName}`);

    // 5. Clear old skin CSS variables (removes stale variables from previous skin)
    if (skinChanged) {
      clearSkinVariables();
    }

    // 6. Apply new skin variables to CSS (use actual skin name, not requested)
    applySkinVariables(skinData, actualSkinName);
    debugLog(1, `[ThemeLoader] Applied skin variables for: ${actualSkinName}`);

    // 7. NOW remove old theme CSS (after new CSS is fully loaded and applied)
    // Only remove if themes are different to avoid removing currently needed CSS
    if (skinChanged && previousThemeName && previousThemeName !== themeName) {
      removeThemeCSS(previousThemeName);
      debugLog(1, `[ThemeLoader] Removed old theme CSS: ${previousThemeName}`);
    }

    // 8. Store current skin name for future comparisons (use actual skin name)
    storeSkinName(actualSkinName);

    debugLog(1, `[ThemeLoader] Theme initialization complete!`);
  } catch (error) {
    debugError('[ThemeLoader] Failed to initialize theme:', error);
    throw error;
  }
}
